import { AccessTokenStore } from '@termly-web/common'

const MILLISECONDS_PER_SECOND = 1_000
const REFRESH_LEAD_MS = 60 * MILLISECONDS_PER_SECOND

// `setTimeout` takes its delay as a 32-bit signed integer, by specification
// rather than by platform, and anything longer wraps to a value it can hold —
// which fires the timer at once.
const LONGEST_TIMEOUT_MS = ( 2 ** 31 ) - 1


/**
 * Says how long to wait before replacing the token held.
 *
 * @returns {Number|undefined} milliseconds to wait, or nothing at all when
 *   there is nothing worth waiting for
 */
export default function getRefreshDelay() {
  const expiresIn = AccessTokenStore.getExpiresIn()

  // No token held reads as `null` and an unparseable lifetime reads as `NaN`.
  if ( !Number.isFinite(expiresIn) ) {
    return
  }

  // A token that has already expired belongs to the response interceptor, not
  // here: the next request carrying it comes back 401 and gets replayed with a
  // fresh one. Scheduling for it would mean refreshing with no wait at all,
  // which is the loop guarded against below.
  if ( expiresIn <= 0 ) {
    return
  }

  const remainingMS = expiresIn * MILLISECONDS_PER_SECOND

  // Normally: refresh a minute before the token dies, which is enough time for
  // the refresh to finish before anything needs the replacement.
  //
  // That subtraction stops working on a token that does not live a full minute,
  // where it asks for a refresh immediately. The replacement would be just as
  // short-lived, so it would ask again immediately, and again after that — a
  // loop, in every open tab, aimed at the auth service. Nothing in our repos
  // sets the lifetime: it is an Authentik provider default, so it takes an
  // admin-UI edit rather than a commit to land us there.
  //
  // Refreshing halfway through instead keeps the loop from forming, because the
  // wait can never fall below half of whatever life the token has. Tokens
  // living longer than two minutes are unaffected — the minute's lead is
  // further out than the halfway point, so it still decides.
  const delay = Math.max(remainingMS - REFRESH_LEAD_MS, remainingMS / 2)

  // The same loop waits at the other end of the range: a wait too long to hold
  // fires at once, and the replacement token would be just as long-lived. The
  // ceiling costs a token nothing, since it only ever refreshes earlier than
  // asked.
  return Math.min(delay, LONGEST_TIMEOUT_MS)
}
